import { useState, useEffect } from "react";
import { AlertTriangle, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface OrderCountdownTimerProps {
  orderCreatedAt: Date;
  orderStatus: string;
  deadlineDays?: number;
}

export function OrderCountdownTimer({ 
  orderCreatedAt, 
  orderStatus, 
  deadlineDays = 2 
}: OrderCountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<{ hours: number; minutes: number; seconds: number; expired: boolean } | null>(null);

  useEffect(() => {
    // Only show countdown for pending orders
    if (orderStatus !== 'pending') {
      setTimeLeft(null);
      return;
    }

    const calculateTimeLeft = () => {
      const deadline = new Date(orderCreatedAt);
      deadline.setDate(deadline.getDate() + deadlineDays);
      
      const now = new Date();
      const diff = deadline.getTime() - now.getTime();

      if (diff <= 0) {
        return { hours: 0, minutes: 0, seconds: 0, expired: true };
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      return { hours, minutes, seconds, expired: false };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [orderCreatedAt, orderStatus, deadlineDays]);

  if (!timeLeft || orderStatus !== 'pending') {
    return null;
  }

  const isUrgent = timeLeft.hours < 12 && !timeLeft.expired;
  const isCritical = timeLeft.hours < 6 && !timeLeft.expired;

  if (timeLeft.expired) {
    return (
      <Badge variant="destructive" className="flex items-center gap-1 text-xs">
        <AlertTriangle className="h-3 w-3" />
        Overdue
      </Badge>
    );
  }

  const formatTime = () => {
    if (timeLeft.hours >= 24) {
      const days = Math.floor(timeLeft.hours / 24);
      const remainingHours = timeLeft.hours % 24;
      return `${days}d ${remainingHours}h`;
    }
    return `${timeLeft.hours}h ${timeLeft.minutes}m`;
  };

  return (
    <Badge 
      variant={isCritical ? "destructive" : isUrgent ? "secondary" : "outline"}
      className={`flex items-center gap-1 text-xs ${
        isCritical ? "animate-pulse" : ""
      } ${isUrgent && !isCritical ? "border-orange-300 text-orange-700 bg-orange-50" : ""}`}
    >
      <Clock className="h-3 w-3" />
      {formatTime()}
    </Badge>
  );
}

export function getCountdownStatus(orderCreatedAt: Date, deadlineDays: number = 2): 'ok' | 'urgent' | 'critical' | 'expired' {
  const deadline = new Date(orderCreatedAt);
  deadline.setDate(deadline.getDate() + deadlineDays);
  
  const now = new Date();
  const diff = deadline.getTime() - now.getTime();
  const hoursLeft = diff / (1000 * 60 * 60);

  if (hoursLeft <= 0) return 'expired';
  if (hoursLeft < 6) return 'critical';
  if (hoursLeft < 12) return 'urgent';
  return 'ok';
}
